/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Popover,
  Button,
  DatePicker,
  Box,
  Scrollable,
  OptionList,
  useBreakpoints,
  Icon,
  Tooltip,
  TextField,
  InlineGrid,
} from "@shopify/polaris";
import { CalendarIcon, InfoIcon } from "@shopify/polaris-icons";
import dayjs from "dayjs";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface DateRangeProps {
  // Custom date ranges/presets (optional - will use default if not provided)
  customRanges?: Array<{
    title: string;
    alias: string;
    period: { since: Date; until: Date };
  }>;
  // Maximum days between start and end date (default: 60)
  maxDaysRange?: number;
  // Default date range if no URL params (default: last 7 days)
  defaultRange?: { alias: string; title: string; period: { since: Date; until: Date } };
  // Custom date param names (default: "bookingfrom" and "bookingto")
  dateParamNames?: { from: string; to: string };
  // Show info tooltip (default: true)
  showInfoTooltip?: boolean;
  // Popover width (default: 800px)
  popoverWidth?: string;
}

export default function DateRange({
  customRanges,
  maxDaysRange = 60,
  defaultRange,
  dateParamNames = { from: "bookingfrom", to: "bookingto" },
  showInfoTooltip = true,
  popoverWidth = "800px",
}: DateRangeProps = {}) {
  const navigateTo = useNavigate();
  const { lgUp } = useBreakpoints();
  const shouldShowMultiMonth = lgUp;
  const params = new URLSearchParams(window.location.search);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");


  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  // Default date ranges - used if customRanges is not provided
  const defaultRanges = [
    { title: "Today", alias: "today", period: { since: new Date(today), until: new Date(today) } },
    {
      title: "Yesterday",
      alias: "yesterday",
      period: { since: new Date(yesterday), until: new Date(yesterday) },
    },
    // {
    //   title: "Last 30 minutes",
    //   alias: "last30minutes",
    //   period: {
    //     since: new Date(today.getTime() - 30 * 60 * 1000),
    //     until: today,
    //   },
    // },
    // {
    //   title: "Last 12 hours",
    //   alias: "last12hours",
    //   period: {
    //     since: new Date(today.getTime() - 12 * 60 * 60 * 1000),
    //     until: today,
    //   },
    // },
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 6)), // 7 days including today
        until: new Date(today), // Include today
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 30)),
        until: yesterday,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 90)),
        until: yesterday,
      },
    },
    {
      title: "Last 365 days",
      alias: "last365days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 365)),
        until: yesterday,
      },
    },
    {
      title: "Last 12 months",
      alias: "last12months",
      period: {
        since: new Date(today.getFullYear(), today.getMonth() - 12, today.getDate()),
        until: yesterday,
      },
    },
    {
      title: "Last week",
      alias: "lastweek",
      period: {
        since: new Date(new Date().setDate(today.getDate() - today.getDay() - 7)),
        until: new Date(new Date().setDate(today.getDate() - today.getDay() - 1)),
      },
    },
    {
      title: "Last month",
      alias: "lastmonth",
      period: {
        since: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        until: new Date(today.getFullYear(), today.getMonth(), 0),
      },
    },
  ];

  // Use custom ranges if provided, otherwise use default ranges
  const ranges = customRanges || defaultRanges;

  // Get default range - use provided one or create a default last 7 days range
  const getDefaultRange = () => {
    if (defaultRange) return defaultRange;
    const today = new Date();
    const sixDaysAgo = new Date();
    sixDaysAgo.setDate(today.getDate() - 6);
    return {
      alias: "last7days",
      title: "Last 7 days",
      period: { since: sixDaysAgo, until: today },
    };
  };

  // Initialize state from URL params or default to last 7 days
  const getInitialDateRange = () => {
    const bookingFrom = params.get(dateParamNames.from);
    const bookingTo = params.get(dateParamNames.to);
    
    if (bookingFrom && bookingTo) {
      const since = new Date(bookingFrom);
      const until = new Date(bookingTo);
      // Check if it matches any preset
      const matchedRange = ranges.find(
        (range) =>
          range.period.since.toDateString() === since.toDateString() &&
          range.period.until.toDateString() === until.toDateString()
      );
      if (matchedRange) {
        return {
          activeDateRange: matchedRange,
          selectedDates: { start: since, end: until },
        };
      }
      // Custom range
      return {
        activeDateRange: {
          alias: "custom",
          title: "Custom",
          period: { since, until },
        },
        selectedDates: { start: since, end: until },
      };
    }
    // Use default range
    const defaultRangeValue = getDefaultRange();
    return {
      activeDateRange: defaultRangeValue,
      selectedDates: { start: defaultRangeValue.period.since, end: defaultRangeValue.period.until },
    };
  };

  const initial = getInitialDateRange();
  const [popoverActive, setPopoverActive] = useState(false);
  const [activeDateRange, setActiveDateRange] = useState(initial.activeDateRange);
  const [selectedDates, setSelectedDates] = useState<any>(initial.selectedDates);
  // Temporary dates for selection (not applied until Apply is clicked)
  const [tempSelectedDates, setTempSelectedDates] = useState<any>(initial.selectedDates);
  const [tempActiveDateRange, setTempActiveDateRange] = useState(initial.activeDateRange);
  const [{ month, year }, setDatePickerMonth] = useState({
    month: initial.selectedDates?.start?.getMonth() ?? today.getMonth(),
    year: initial.selectedDates?.start?.getFullYear() ?? today.getFullYear(),
  });

  // Format dates for display in button (e.g. "Sat Feb 25, 2025 - Sun Mar 26, 2026")
  const formatDateRange = (range: any) => {
    const format = "ddd MMM D, YYYY";
    return `${dayjs(range.period.since).format(format)} - ${dayjs(range.period.until).format(format)}`;
  };

  useEffect(() => {
    const bookingFrom = params.get(dateParamNames.from);
    const bookingTo = params.get(dateParamNames.to);
    
    if (!bookingFrom || !bookingTo) {
      // Set default dates
      const defaultRangeValue = getDefaultRange();
      params.set(dateParamNames.from, defaultRangeValue.period.since.toDateString());
      params.set(dateParamNames.to, defaultRangeValue.period.until.toDateString());
      setActiveDateRange(defaultRangeValue);
      setSelectedDates({ start: defaultRangeValue.period.since, end: defaultRangeValue.period.until });
      navigateTo({ search: params.toString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .Polaris-Popover__Content {
        max-width: ${popoverWidth} !important;
        width: ${popoverWidth} !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [popoverWidth]);

  // Reset temp dates when popover opens
  useEffect(() => {
    if (popoverActive) {
      setTempSelectedDates(selectedDates);
      setTempActiveDateRange(activeDateRange);
    }
  }, [popoverActive]);

  const handleDateChange = useCallback((dt: any) => {
    // Only update temporary dates, don't apply to URL yet
    setTempSelectedDates(dt);
    
    // Update month/year to show the start date's month
    setDatePickerMonth({
      month: dt.start.getMonth(),
      year: dt.start.getFullYear(),
    });
    
    // Check if it matches any preset
    const matchedRange = ranges.find(
      (range) =>
        range.period.since.toDateString() === dt.start.toDateString() &&
        range.period.until.toDateString() === dt.end.toDateString()
    );
    
    if (matchedRange) {
      setTempActiveDateRange(matchedRange);
    } else {
      // Custom range
      setTempActiveDateRange({
        alias: "custom",
        title: "Custom",
        period: { since: dt.start, until: dt.end },
      });
    }
  }, []);

  const handleMonthChange = useCallback((newMonth: number, newYear: number) => {
    setDatePickerMonth({ month: newMonth, year: newYear });
  }, []);

  const handleRangeSelect = useCallback((value: string[]) => {
    const selectedRange = ranges.find((r) => r.alias === value[0]);
    if (selectedRange) {
      // Only update temporary state, don't apply to URL yet
      setTempActiveDateRange(selectedRange);
      setTempSelectedDates({
        start: selectedRange.period.since,
        end: selectedRange.period.until,
      });
      // Update month/year to show the start date's month
      setDatePickerMonth({
        month: selectedRange.period.since.getMonth(),
        year: selectedRange.period.since.getFullYear(),
      });
    }
  }, []);

  const handleApply = useCallback(() => {
    // Apply the temporary dates to actual state and URL
    setActiveDateRange(tempActiveDateRange);
    setSelectedDates(tempSelectedDates);
    
    params.set(dateParamNames.from, tempSelectedDates.start.toDateString());
    params.set(dateParamNames.to, tempSelectedDates.end.toDateString());
    navigateTo({ search: params.toString() });
    
    // Close popover
    setPopoverActive(false);
  }, [tempActiveDateRange, tempSelectedDates, params, navigateTo, dateParamNames]);

  const handleCancel = useCallback(() => {
    // Revert to original dates
    setTempSelectedDates(selectedDates);
    setTempActiveDateRange(activeDateRange);
    
    // Close popover
    setPopoverActive(false);
  }, [selectedDates, activeDateRange]);

  const buttonText = formatDateRange(activeDateRange);
  const defaultRangeValue = getDefaultRange();
  const isDefaultRange = !params.get(dateParamNames.from) || activeDateRange.alias === defaultRangeValue.alias;

  // Calculate max end date for date disabling
  const maxEndDate = tempSelectedDates?.start
    ? (() => {
        const max = new Date(tempSelectedDates.start);
        max.setDate(max.getDate() + maxDaysRange);
        return max;
      })()
    : undefined;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "start",
        alignItems: "center",
        gap: "10px",
      }}
    >
      {isDefaultRange && showInfoTooltip && (
        <div
          style={{
            fontWeight: "normal",
            fontSize: "16px",
            fontStyle: "italic",
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <div style={{ cursor: "pointer" }}>
            <Tooltip
              width="wide"
              content={
                <p style={{ textAlign: "center" }}>
                  By default, you will see last seven days booked orders
                  analytics. But you can 👉 change filters however you want.
                </p>
              }
            >
              <Icon source={InfoIcon}></Icon>
            </Tooltip>
          </div>
        </div>
      )}
      <Popover
        active={popoverActive}
        preferredAlignment="left"
        preferredPosition="below"
        activator={
          <Button onClick={() => setPopoverActive(!popoverActive)} icon={CalendarIcon}>
            {buttonText}
          </Button>
        }
        onClose={() => {
          // Revert to original dates when closing without Apply
          setTempSelectedDates(selectedDates);
          setTempActiveDateRange(activeDateRange);
          setPopoverActive(false);
        }}
        
      >
        <div style={{ display: "flex", width: popoverWidth, minWidth: popoverWidth, maxWidth: popoverWidth }}>
          <Popover.Pane fixed>
            <div style={{ width: "250px" }}>
              <Scrollable style={{ maxHeight: "400px" }}>
                <OptionList
                  options={ranges.map(({ alias, title }) => ({
                    value: alias,
                    label: title,
                  }))}
                  selected={[tempActiveDateRange.alias]}
                  onChange={handleRangeSelect}
                />
              </Scrollable>
            </div>
          </Popover.Pane>
          <Popover.Pane>
            <div style={{ width: "550px", overflow: "visible" }}>
              <Box padding="400">
                <InlineGrid columns={{ xs: 1, md: 2, lg: 2, xl: 2 }} gap="400">
                <TextField autoComplete="off" label="Start Date" labelHidden value={tempSelectedDates?.start?.toDateString()} onChange={(value) => {
                  setTempSelectedDates({
                    start: new Date(value),
                    end: tempSelectedDates?.end,
                  })
                }} />
                <TextField autoComplete="off" label="End Date" labelHidden value={tempSelectedDates?.end?.toDateString()} onChange={(value) => {
                  setTempSelectedDates({
                    start: tempSelectedDates?.start,
                    end: new Date(value),
                  });
                }} />
                </InlineGrid>
                <div style={{padding: "10px 0px"}}></div>
                <DatePicker
                  month={month}
                  year={year}
                  selected={tempSelectedDates}
                  onChange={handleDateChange}
                  onMonthChange={handleMonthChange}
                  allowRange
                  multiMonth={shouldShowMultiMonth}
                  disableDatesAfter={maxEndDate}
                />
                {/* Cancel and Apply buttons */}
                <div style={{ 
                  display: "flex", 
                  justifyContent: "flex-end", 
                  gap: "8px", 
                  paddingTop: "16px",
                  borderTop: "1px solid #e1e3e5"
                }}>
                  <Button onClick={handleCancel}>Cancel</Button>
                  <Button variant={theme === "dark" ? "secondary" : "primary"} onClick={handleApply}>Apply</Button>
                </div>
              </Box>
            </div>
          </Popover.Pane>
        </div>
      </Popover>
    </div>
  );
}
